import React, { useState, useEffect, memo, useCallback, useMemo } from "react";
import {
  Typography,
  Button,
  Box,
  Skeleton,
  Snackbar,
  Alert,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
} from "@mui/material";
import {
  Share,
  AutoAwesome,
  SwapHoriz,
  FolderOpenOutlined,
  Check,
} from "@mui/icons-material";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/EnterpriseAuthContext";
import { useAIRequests } from "../context/AIRequestContext";
import {
  getExtractionTypeOptions,
  isExtractionConfigValid,
  extractionConfigToLegacyExtractionTypeArray,
  normalizeExtractionConfig,
} from "../utils/extractionTaxonomy";
import edgeFunctionService from "../services/edgeFunctionService";
import enhancedExportService from "../services/enhancedExportService";
import { formatDate } from "../utils/dateUtils";
import { openDocumentEditor } from "../utils/navigation";
import ExportDropdown from "./ExportDropdown";
import DocumentPreviewDialog from "./DocumentPreviewDialog";
import SessionsTab from "./SessionsTab";
import RequirementsTab, { countRequirementsNeedingReview } from "./RequirementsTab";
import DocumentsTab from "./DocumentsTab";
import ShareProjectDialog from './ShareProjectDialog';
import EntityBreadcrumbs from "./layout/EntityBreadcrumbs";
import PipelineStepper from "./layout/PipelineStepper";
import { PageContainer, PageHeader, ConfirmDialog } from "./pagekit";
import mammoth from 'mammoth';
import useDocumentUpdatedListener from './editor/useDocumentUpdatedListener';
import { documentExportOptions } from './documents/documentExportOptions';
import { htmlToPlainText } from './sessions/transcriptUtils';

/** Tab keys in pipeline order; the URL `?tab=` uses these names. */
const TAB_NAMES = ['sessions', 'requirements', 'documents'];

/** Pill shape for page-header buttons (matches the project mockup). */
const HEADER_BUTTON_SX = { borderRadius: 'var(--q-radius-pill, 999px)', minHeight: 38, px: 2 };

/** Accessible names of the pipeline steps. */
const STEP_LABELS = { sessions: 'Sessions', requirements: 'Requirements', documents: 'Documents' };

/**
 * Project page: header, the Capture -> Distill -> Generate pipeline (which
 * doubles as the tab bar and is synced to `?tab=`), and the active step.
 * @returns {JSX.Element}
 */
const ProjectDetail = memo(() => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state, actions } = useApp();
  const { state: authState } = useAuth();
  const { refreshRequests } = useAIRequests();
  const [project, setProject] = useState(null);

  /**
   * Resolve the full customer object (with domain array) for the current project.
   * Falls back to the joined customer data on the project if not found in global state.
   */
  const projectCustomer = useMemo(() => {
    if (!project?.customer_id) return null;
    return state.customers.find((c) => c.id === project.customer_id) || project.customer || null;
  }, [project, state.customers]);

  /** Other projects of the same customer, for the project switcher. */
  const siblingProjects = useMemo(() => {
    if (!project) return [];
    return (state.projects || [])
      .filter((p) => (p.customer_id || null) === (project.customer_id || null))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [project, state.projects]);

  const tabParam = searchParams.get('tab');
  const activeTab = TAB_NAMES.includes(tabParam) ? tabParam : 'sessions';

  const [extracting, setExtracting] = useState(false);
  const [extractionError, setExtractionError] = useState("");
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [previewTitle, setPreviewTitle] = useState("");
  /** The previewed document (its page_setup and created date feed the export's header and footer). */
  const [previewDocument, setPreviewDocument] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [switcherAnchor, setSwitcherAnchor] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [deletingDocument, setDeletingDocument] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  // One-shot request for the Sessions step ('create' | 'extract'), set by the pipeline button.
  const [sessionsAction, setSessionsAction] = useState(null);

  useEffect(() => {
    const foundProject = state.projects.find((p) => p.id === projectId);
    if (foundProject) {
      setProject(foundProject);
    } else if (state.initialLoadComplete && !state.loading) {
      // Only redirect if we've completed initial data load and project still not found
      navigate("/projects");
    }
  }, [projectId, state.projects, navigate, state.initialLoadComplete, state.loading]);

  // Editor tabs save to the database themselves; only refresh when they report a save.
  useDocumentUpdatedListener((updates) => {
    if (typeof actions.patchGeneratedDocumentLocal === 'function') {
      updates.forEach((u) => actions.patchGeneratedDocumentLocal(u.documentId, {
        content: u.content,
        updatedAt: u.updatedAt,
        ...(u.pageSetup !== undefined ? { pageSetup: u.pageSetup } : {}),
      }));
    } else {
      actions.loadProjects();
    }
  });

  // Keep `?tab=` present so the current step is always linkable.
  useEffect(() => {
    if (!TAB_NAMES.includes(tabParam)) {
      const next = new URLSearchParams(searchParams);
      next.set('tab', activeTab);
      setSearchParams(next, { replace: true });
    }
  }, [tabParam, activeTab, searchParams, setSearchParams]);

  /**
   * Switch pipeline step (tab) and record it in the URL.
   * @param {string} name - One of TAB_NAMES.
   */
  const handleTabChange = useCallback((name) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', name);
    setSearchParams(next);
  }, [searchParams, setSearchParams]);

  /** Canonical extraction labels — server-side extraction does not depend on client API key. */
  const extractionTypes = useMemo(() => getExtractionTypeOptions(), []);

  const readFileContent = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const fileName = file.name.toLowerCase();
      const isWordDocument = fileName.endsWith('.docx') || fileName.endsWith('.doc');

      if (isWordDocument) {
        // Use Mammoth.js for Word documents
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            const arrayBuffer = e.target.result;
            const result = await mammoth.convertToHtml({ arrayBuffer });

            if (result.messages && result.messages.length > 0) {
              console.warn('Mammoth conversion warnings:', result.messages);
            }

            // Plain text for AI processing (inert parse that keeps paragraph breaks)
            resolve(htmlToPlainText(result.value));
          } catch (error) {
            reject(new Error(`Failed to process Word document: ${error.message}`));
          }
        };
        reader.onerror = () => reject(new Error('Failed to read Word document'));
        reader.readAsArrayBuffer(file);
      } else {
        // Use text reader for other files (.txt, .md, etc.)
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = () => reject(new Error("Failed to read file"));
        reader.readAsText(file);
      }
    });
  }, []);

  const handleCreateSession = useCallback(
    async (sessionFormData) => {
      const isRegeneration = !!(sessionFormData.sessionId && sessionFormData.regenerateMode);

      if (
        !sessionFormData.name.trim() ||
        (!isRegeneration && sessionFormData.contentType === "file" && !sessionFormData.file) ||
        (!isRegeneration && sessionFormData.contentType === "paste" &&
          !sessionFormData.pastedContent.trim()) ||
        !isExtractionConfigValid(sessionFormData.extractionConfig)
      )
        return { success: false, error: "Session details are incomplete." };

      setExtracting(true);
      setExtractionError("");

      let sessionId = null;

      try {
        if (isRegeneration) {
          // For regeneration, the transcript already lives in the DB — no file reading needed.
          sessionId = sessionFormData.sessionId;
        } else {
          // Step 1: Read file content client-side (text extraction only)
          let fileContent;
          if (sessionFormData.contentType === "file") {
            fileContent = await readFileContent(sessionFormData.file);
          } else {
            fileContent = sessionFormData.pastedContent;
          }

          // Step 2: Build structured transcript object with content + metadata
          const isFile = sessionFormData.contentType === "file";
          const fileName = isFile ? sessionFormData.file.name.toLowerCase() : null;
          const isWordDoc = fileName && (fileName.endsWith(".docx") || fileName.endsWith(".doc"));

          const transcript = {
            content: fileContent,
            contentType: sessionFormData.contentType,
            source: isFile
              ? isWordDoc
                ? "mammoth"
                : "text-file"
              : "paste",
            ...(isFile && {
              metadata: {
                fileName: sessionFormData.file.name,
                fileSize: sessionFormData.file.size,
                fileType: sessionFormData.file.type,
              },
            }),
            processedAt: new Date().toISOString(),
          };

          // Step 3: Create session with structured transcript object (stored in DB)
          const sessionData = {
            projectId,
            name: sessionFormData.name,
            description: sessionFormData.description,
            file: isFile
              ? {
                  name: sessionFormData.file.name,
                  size: sessionFormData.file.size,
                  type: sessionFormData.file.type,
                }
              : null,
            contentType: sessionFormData.contentType,
            extractionType: extractionConfigToLegacyExtractionTypeArray(
              sessionFormData.extractionConfig
            ),
            extractionConfig: sessionFormData.extractionConfig,
            transcript,
          };

          const sessionResult = await actions.addSession(sessionData);

          if (!sessionResult.success) {
            throw new Error(sessionResult.error);
          }

          sessionId = sessionResult.data.id;
        }

        // Step 4: Dispatch server-side extraction via Edge Function
        const response = await edgeFunctionService.callFunction(
          "extract-requirements",
          {
            sessionId,
            projectId,
            extractionTypes: extractionConfigToLegacyExtractionTypeArray(
              sessionFormData.extractionConfig
            ),
            extractionConfig: sessionFormData.extractionConfig,
            targetedExtraction: sessionFormData.targetedExtraction || "",
            projectContext: project?.description || project?.name || "",
            isRegeneration: isRegeneration || false,
            regenerateMode: sessionFormData.regenerateMode || null,
          }
        );

        if (!response?.success) {
          throw new Error(
            response?.error || "Failed to start requirement extraction"
          );
        }

        // Extraction is now running server-side.
        // Refresh AI requests so the notification panel picks up the new request immediately.
        await refreshRequests();
        setExtractionError("");
        return { success: true, sessionId };
      } catch (error) {
        setExtractionError(error.message);

        // Cleanup session if it was created but dispatch failed (not for regeneration)
        if (sessionId && !isRegeneration) {
          try {
            await actions.deleteSession(projectId, sessionId);
          } catch (cleanupError) {
            console.error("Failed to cleanup session:", cleanupError);
          }
        }
        return { success: false, error: error.message };
      } finally {
        setExtracting(false);
      }
    },
    [actions, projectId, readFileContent, project, refreshRequests]
  );

  const handleDeleteSession = useCallback(
    (sessionId) => {
      actions.deleteSession(projectId, sessionId);
    },
    [actions, projectId]
  );

  /**
   * Update an existing session's editable fields.
   * @param {string} sessionId - The session ID to update.
   * @param {object} updates - Fields to update: { name?, description?, transcript? }.
   * @returns {Promise<{success: boolean, error?: string}>} Result.
   */
  const handleUpdateSession = useCallback(
    async (sessionId, updates) => {
      return actions.updateSession(projectId, sessionId, updates);
    },
    [actions, projectId]
  );

  /**
   * Import a Fathom meeting as a session linked to this project.
   * Creates the session with the transcript stored, sourced from Fathom.
   * @param {object} meetingData - Meeting data with name, transcript, source, sourceMetadata.
   */
  const handleImportFathomSession = useCallback(
    async (meetingData) => {
      const sessionData = {
        projectId,
        name: meetingData.name,
        description: `Imported from Fathom on ${new Date().toLocaleDateString()}`,
        transcript: meetingData.transcript,
        source: meetingData.source || "fathom",
        sourceMetadata: meetingData.sourceMetadata || {},
        extractionType: null,
      };

      const sessionResult = await actions.addSession(sessionData);

      if (!sessionResult.success) {
        throw new Error(sessionResult.error);
      }

      return sessionResult;
    },
    [actions, projectId]
  );

  /**
   * Trigger server-side extraction for an existing session (e.g., imported Fathom meeting).
   * The session already has a transcript stored; this dispatches to the Edge Function.
   * @param {object} params - { sessionId, extractionTypes, targetedExtraction }
   * @returns {Promise<{success: boolean, error?: string}>} Dispatch outcome.
   */
  const handleExtractSession = useCallback(
    async ({ sessionId, extractionConfig, extractionTypes, targetedExtraction }) => {
      setExtracting(true);
      setExtractionError("");

      try {
        const cfg = extractionConfig || normalizeExtractionConfig(null, extractionTypes);
        const response = await edgeFunctionService.callFunction(
          "extract-requirements",
          {
            sessionId,
            projectId,
            extractionTypes:
              extractionTypes ||
              extractionConfigToLegacyExtractionTypeArray(cfg),
            extractionConfig: cfg,
            targetedExtraction: targetedExtraction || "",
            projectContext: project?.description || project?.name || "",
            isRegeneration: false,
            regenerateMode: null,
          }
        );

        if (!response?.success) {
          throw new Error(
            response?.error || "Failed to start requirement extraction"
          );
        }

        // Refresh AI requests so the notification panel picks up the new request immediately.
        await refreshRequests();
        setExtractionError("");
        return { success: true };
      } catch (error) {
        setExtractionError(error.message);
        return { success: false, error: error.message };
      } finally {
        setExtracting(false);
      }
    },
    [projectId, project, refreshRequests]
  );

  const handleAddRequirement = useCallback(
    async (requirementData) => {
      return await actions.addRequirement({
        projectId: projectId,
        sessionId: null, // Manual requirements don't belong to a session
        title: requirementData.title,
        description: requirementData.description,
        type: requirementData.type,
        priority: requirementData.priority,
        acceptanceCriteria: requirementData.acceptanceCriteria,
      });
    },
    [actions, projectId]
  );

  const handleUpdateRequirement = useCallback(
    (requirementId, updates) => actions.updateRequirement(projectId, requirementId, updates),
    [actions, projectId]
  );

  const handleDeleteRequirement = useCallback(
    (requirementId) => actions.deleteRequirement(projectId, requirementId),
    [actions, projectId]
  );

  const handlePreviewDocument = useCallback((document) => {
    setPreviewContent(document.content);
    setPreviewTitle(document.name);
    setPreviewDocument(document);
    setPreviewDialogOpen(true);
  }, []);

  const handleEditDocument = useCallback((document) => {
    openDocumentEditor(navigate, { documentId: document.id, projectId, title: document.name });
  }, [navigate, projectId]);

  const handleDeleteDocument = useCallback((documentId) => {
    setDocumentToDelete(documentId);
    setDeleteConfirmOpen(true);
  }, []);

  const handleConfirmDeleteDocument = useCallback(async () => {
    if (!documentToDelete) return;

    setDeletingDocument(true);
    try {
      await actions.deleteGeneratedDocument(projectId, documentToDelete);
      setSnackbar({ open: true, message: 'Document deleted.', severity: 'success' });
      setDeleteConfirmOpen(false);
      setDocumentToDelete(null);
    } catch (error) {
      setSnackbar({ open: true, message: `Failed to delete document: ${error.message}`, severity: 'error' });
      console.error("Error deleting document:", error);
    } finally {
      setDeletingDocument(false);
    }
  }, [actions, projectId, documentToDelete]);

  const handleCancelDeleteDocument = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDocumentToDelete(null);
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleGenerateDocument = useCallback(() => {
    if (!project) return;
    const params = new URLSearchParams();
    if (project.customer_id) params.set('customerId', project.customer_id);
    params.set('projectId', project.id);
    navigate(`/generate?${params.toString()}`);
  }, [navigate, project]);

  /**
   * Pipeline "next step" button. 'sessions' opens the new-session dialog, 'requirements' opens
   * the extract dialog for the first session with nothing extracted, 'documents' opens the
   * generator. Session actions switch to the Sessions step, which runs them once mounted.
   * @param {string} step - Step the next action belongs to.
   */
  const handleNextAction = useCallback((step) => {
    if (step === 'documents') {
      handleGenerateDocument();
      return;
    }
    setSessionsAction(step === 'requirements' ? 'extract' : 'create');
    handleTabChange('sessions');
  }, [handleGenerateDocument, handleTabChange]);

  const clearSessionsAction = useCallback(() => setSessionsAction(null), []);

  const isOwner = Boolean(project && project.userId === authState.user?.id);

  if (!project) {
    return (
      <PageContainer aria-busy="true" aria-label="Loading project">
        <Skeleton variant="text" width={220} height={20} />
        <Skeleton variant="text" width="45%" height={44} />
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={68} sx={{ borderRadius: 'var(--q-radius-card)' }} />
          ))}
        </Box>
        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 'var(--q-radius-card)' }} />
      </PageContainer>
    );
  }

  const sessionCount = project.sessions?.length || 0;
  const requirementCount = project.requirements?.length || 0;
  const documentCount = project.generatedDocuments?.length || 0;
  const needsReviewCount = countRequirementsNeedingReview(project.requirements || []);

  const pipelineSummaries = {
    sessions: sessionCount ? `${sessionCount} session${sessionCount === 1 ? '' : 's'}` : 'Add a meeting or file',
    requirements: requirementCount
      ? `${requirementCount} extracted${needsReviewCount ? ` · ${needsReviewCount} to review` : ''}`
      : 'None yet',
    documents: documentCount ? `${documentCount} generated` : 'None yet',
  };

  const metaParts = [projectCustomer?.name, project.createdAt ? `Created ${formatDate(project.createdAt)}` : null]
    .filter(Boolean)
    .join(' · ');

  return (
    <PageContainer>
      <PageHeader
        breadcrumbs={<EntityBreadcrumbs projectId={project.id} customerId={project.customer_id || undefined} />}
        title={project.name}
        actions={
          <>
            {siblingProjects.length > 1 && (
              <Tooltip title="Switch project">
                <Button
                  variant="text"
                  color="inherit"
                  startIcon={<SwapHoriz />}
                  onClick={(e) => setSwitcherAnchor(e.currentTarget)}
                  aria-haspopup="menu"
                  aria-expanded={Boolean(switcherAnchor)}
                  sx={{ ...HEADER_BUTTON_SX, color: 'text.secondary' }}
                >
                  Switch
                </Button>
              </Tooltip>
            )}
            {isOwner && (
              <Button
                variant="outlined"
                startIcon={<Share />}
                onClick={() => setShareDialogOpen(true)}
                sx={HEADER_BUTTON_SX}
              >
                Share
              </Button>
            )}
            <Tooltip title={requirementCount === 0 ? 'Add or extract requirements first' : ''}>
              <span>
                <Button
                  variant="contained"
                  startIcon={<AutoAwesome />}
                  onClick={handleGenerateDocument}
                  disabled={requirementCount === 0}
                  sx={HEADER_BUTTON_SX}
                >
                  Generate document
                </Button>
              </span>
            </Tooltip>
          </>
        }
        description={
          [metaParts, project.description].filter(Boolean).length ? (
            <>
              {metaParts && <Box component="span" sx={{ display: 'block' }}>{metaParts}</Box>}
              {project.description && (
                <Box component="span" sx={{ display: 'block', color: 'text.primary', mt: 0.5 }}>
                  {project.description}
                </Box>
              )}
            </>
          ) : null
        }
      />

      <PipelineStepper
        project={project}
        active={activeTab}
        summaries={pipelineSummaries}
        onStepSelect={handleTabChange}
        onNextAction={handleNextAction}
        nextActionLabels={{ sessions: 'Add a session', requirements: 'Extract requirements', documents: 'Generate document' }}
      />

      <Box component="section" aria-label={STEP_LABELS[activeTab]} sx={{ minWidth: 0 }}>
        {activeTab === 'sessions' && (
          <SessionsTab
            project={project}
            projectCustomer={projectCustomer}
            onCreateSession={handleCreateSession}
            onUpdateSession={handleUpdateSession}
            onDeleteSession={handleDeleteSession}
            onExtractSession={handleExtractSession}
            onUpdateCustomer={actions.updateCustomer}
            findCustomerByDomain={actions.findCustomerByDomain}
            extracting={extracting}
            extractionError={extractionError}
            extractionTypes={extractionTypes}
            onImportFathomSessions={handleImportFathomSession}
            onGoToRequirements={() => handleTabChange('requirements')}
            pendingAction={sessionsAction}
            onPendingActionHandled={clearSessionsAction}
          />
        )}

        {activeTab === 'requirements' && (
          <RequirementsTab
            project={project}
            onAddRequirement={handleAddRequirement}
            onUpdateRequirement={handleUpdateRequirement}
            onDeleteRequirement={handleDeleteRequirement}
            onShareProject={isOwner ? () => setShareDialogOpen(true) : null}
            onExportRequirements={(requirements, projectName) => {
              const baseFileName = `${projectName}-requirements`.replace(/[^a-z0-9]/gi, '_').toLowerCase();
              enhancedExportService.exportRequirements(requirements, projectName, `${baseFileName}.docx`);
            }}
            onGenerateDocument={handleGenerateDocument}
            onGoToSessions={() => handleTabChange('sessions')}
            extractionTypes={extractionTypes}
          />
        )}

        {activeTab === 'documents' && (
          <DocumentsTab
            project={project}
            onPreviewDocument={handlePreviewDocument}
            onEditDocument={handleEditDocument}
            onDeleteDocument={handleDeleteDocument}
          />
        )}
      </Box>

      {/* Project switcher */}
      <Menu
        anchorEl={switcherAnchor}
        open={Boolean(switcherAnchor)}
        onClose={() => setSwitcherAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 260, maxHeight: 420 } }}
      >
        <Typography variant="overline" color="text.secondary" sx={{ px: 2, py: 0.5, display: 'block' }}>
          {projectCustomer?.name || 'Projects'}
        </Typography>
        {siblingProjects.map((p) => (
          <MenuItem
            key={p.id}
            selected={p.id === project.id}
            onClick={() => {
              setSwitcherAnchor(null);
              if (p.id !== project.id) navigate(`/projects/${p.id}?tab=${activeTab}`);
            }}
          >
            <ListItemIcon>
              {p.id === project.id ? <Check fontSize="small" /> : <FolderOpenOutlined fontSize="small" />}
            </ListItemIcon>
            <ListItemText
              primary={p.name}
              secondary={`${p.requirements?.length || 0} requirements`}
              primaryTypographyProps={{ noWrap: true }}
            />
          </MenuItem>
        ))}
        <Divider />
        <MenuItem onClick={() => { setSwitcherAnchor(null); navigate('/projects'); }}>
          <ListItemText inset primary="All projects" />
        </MenuItem>
      </Menu>

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        title={previewTitle}
        content={previewContent}
        actions={
          <ExportDropdown
            htmlContent={previewContent}
            fileName={`${previewTitle}.docx`}
            projectName={project.name}
            buttonText="Export"
            {...documentExportOptions(previewDocument || { name: previewTitle }, {
              projectName: project.name,
              customerName: projectCustomer?.name || '',
            })}
          />
        }
      />

      <ShareProjectDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        project={project}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete document?"
        message="This generated document will be removed from the project. This cannot be undone."
        confirmLabel="Delete document"
        busy={deletingDocument}
        onConfirm={handleConfirmDeleteDocument}
        onClose={handleCancelDeleteDocument}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
});

ProjectDetail.displayName = "ProjectDetail";

export default ProjectDetail;
